import { useBillboardStore } from '../../store/billboardStore';
import { getBillboard, getBillboardForOwner, updateBillboard } from '../../api/billboardApi';
import { BillboardData } from '../../dto/EntityRepository';
import { BillboardResponse } from '../../dto/ResDtoRepository';
import { UpdateBillboardReq } from '../../dto/ReqDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UseBillboardResult {
  billboard: BillboardData | null;
  getBillboard: () => Promise<void>;
  getBillboardForOwner: () => Promise<void>;
  updateBillboard: (data: UpdateBillboardReq) => Promise<void>;
}

export const useBillboard = (): UseBillboardResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { billboard, setBillboard } = useBillboardStore();
  const { setUpdatedTempAlert } = useTempAlertStore();
  const withApiHandler = useApiWrapper();

  const handleGetBillboardSuccess = (response: BillboardResponse) => {
    const billboardData = response.data;
    setBillboard(billboardData);
  };

  const handleUpdateBillboardSuccess = (response: BillboardResponse) => {
    const billboardData = response.data;
    setBillboard(billboardData);
    setUpdatedTempAlert();
  };

  const handleBillboardRequest = async (
    action: 'get' | 'get_for_owner' | 'update',
    data?: UpdateBillboardReq
  ) => {
    if (!pageOwner) return;
    const { aui } = pageOwner;
    const apiFunction = async () => {
      switch (action) {
        case 'update':
          if (!data) throw new Error('Data is required for update');
          const updateData = data satisfies UpdateBillboardReq;
          return runCurrentArchiveRequest(
            aui,
            () => updateBillboard(aui, updateData),
            handleUpdateBillboardSuccess,
          );
        case 'get_for_owner':
          return runCurrentArchiveRequest(
            aui,
            () => getBillboardForOwner(aui),
            handleGetBillboardSuccess,
          );
        case 'get':
        default:
          return runCurrentArchiveRequest(
            aui,
            () => getBillboard(aui),
            handleGetBillboardSuccess,
          );
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getBillboardHandler = () => handleBillboardRequest('get');
  const getBillboardForOwnerHandler = () => handleBillboardRequest('get_for_owner');
  const updateBillboardHandler = (data: UpdateBillboardReq) => handleBillboardRequest('update', data);

  return {
    billboard,
    getBillboard: getBillboardHandler,
    getBillboardForOwner: getBillboardForOwnerHandler,
    updateBillboard: updateBillboardHandler,
  };
}
